import { useCallback, useEffect, useState, type ReactNode } from "react"

import {
  api,
  type AndamentoDaImportacao,
  type CategoriaDoAthus,
  type CategoriasDoAthus,
  type EspelhoDoAthus,
  type ExecucaoDaImportacao,
  type ImportacaoAutomatica,
} from "../api"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../componentes/AlertDialog"
import { Abas, type DescricaoDeAba } from "../componentes/Abas"
import { Alert, AlertDescription, AlertTitle } from "../componentes/Alert"
import { Badge } from "../componentes/Badge"
import { mensagemDeRecusa } from "../componentes/BlocoDeCampos"
import { Button } from "../componentes/Button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../componentes/Collapsible"
import { Escolha } from "../componentes/Escolha"
import * as Icones from "../componentes/Icones"
import { Input } from "../componentes/Input"
import { Numero, Numeros } from "../componentes/Numeros"
import { Progress } from "../componentes/Progress"
import { RotuloDoCampo } from "../componentes/RotuloDoCampo"
import { Selo } from "../componentes/Selo"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../componentes/Sheet"
import { Bloco, CarregandoNaSecao, FalhaNaSecao, Parte } from "../componentes/Secao"
import { Skeleton } from "../componentes/Skeleton"
import { Switch } from "../componentes/Switch"
import { Column, TablePrincipal2, TablePrincipalFiltroGlobal } from "../componentes/TablePrincipal2"
import { toast } from "../componentes/Toast"
import { data, dataHora, hora, inteiro, plural } from "../formato"
import { BOTAO_BRANCO, CABECALHO_DO_SHEET } from "../lib/classes"
import { cn } from "../lib/cn"

const DADO_QUE_NAO_SAI: Record<string, string> = {
  ip: "endereço de rede do computador",
  computador: "nome do computador",
  tamanhotela: "tamanho da tela",
  timezone: "fuso horário",
  language: "idioma do navegador",
  distancia_ip: "distância calculada pelo endereço de rede",
  gps_ip: "localização por GPS",
  cidade_uf_ip: "cidade e estado de onde a pessoa acessou",
  whois_ip: "a quem pertence o endereço de rede",
  assinaturabase64: "imagem da assinatura",
}

function LinhaDaLigacao({
  Icone,
  rotulo,
  children,
}: {
  Icone: Icones.Icone
  rotulo: string
  children: ReactNode
}) {
  return (
    <div className="flex flex-col px-4 py-2.5 sm:flex-row sm:items-center">
      <div className="mb-1 flex w-full items-center gap-2 text-sm font-medium text-muted-foreground sm:mb-0 sm:w-1/3">
        <Icone />
        <span>{rotulo}</span>
      </div>
      <div className="w-full text-sm text-foreground sm:w-2/3">{children}</div>
    </div>
  )
}

const ETAPA_DA_IMPORTACAO: Record<AndamentoDaImportacao["etapa"], string> = {
  importando: "Trazendo os documentos do ATHUS",
  lendo: "Lendo os documentos",
  processando: "Calculando a vigência e os avisos",
}

function haQuantoTempo(desde: string | null): string | null {
  if (desde === null) return null
  const minutos = Math.floor((Date.now() - new Date(desde).getTime()) / 60000)
  if (Number.isNaN(minutos)) return null
  return minutos < 1 ? "há menos de 1 min" : minutos < 60 ? `há ${minutos} min` : `há ${Math.floor(minutos / 60)} h ${minutos % 60} min`
}

function Andamento({ desde, andamento }: { desde: string | null; andamento: AndamentoDaImportacao | null }) {
  const comTotal = andamento !== null && andamento.total !== null && andamento.total > 0 && andamento.feitos !== null
  const valor = comTotal ? Math.min(100, Math.round(((andamento.feitos ?? 0) / (andamento.total ?? 1)) * 100)) : null
  const etapa = andamento === null ? "Começando" : ETAPA_DA_IMPORTACAO[andamento.etapa]
  return (
    <div role="status" className="flex flex-col gap-2 rounded-md border border-(--borda) p-3 text-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-semibold">
          Importando desde {hora(desde)}
          {haQuantoTempo(desde) !== null && <span className="font-normal text-(--tinta-2)"> ({haQuantoTempo(desde)})</span>}
        </span>
        <span className="text-(--tinta-2)">
          {etapa}
          {comTotal && `: ${inteiro(andamento.feitos ?? 0)} de ${inteiro(andamento.total ?? 0)}`}
        </span>
      </div>
      <Progress value={valor} aria-label="Andamento da importação" />
    </div>
  )
}

type Confirmacao = "ligar" | "desligar" | "importar"

const TITULO_DA_IMPORTACAO = "Importação dos contratos"

const BOTAO_DE_ACAO = "border-primary bg-card text-primary hover:bg-(--azul-50) hover:text-primary"

const BOTAO_CINZA = "border-(--borda-forte) bg-card text-(--tinta-2) hover:bg-(--cinza-100) hover:text-(--tinta)"

function ImportacaoNoRelogio() {
  const [estado, definirEstado] = useState<ImportacaoAutomatica | null>(null)
  const [falha, definirFalha] = useState<string | null>(null)
  const [ligada, definirLigada] = useState(false)
  const [entidadeId, definirEntidadeId] = useState("")
  const [limite, definirLimite] = useState("")
  const [gravando, definirGravando] = useState(false)
  const [confirmando, definirConfirmando] = useState<Confirmacao | null>(null)
  const [vendoHistorico, definirVendoHistorico] = useState(false)

  const receber = (novo: ImportacaoAutomatica) => {
    definirEstado(novo)
    definirLigada(novo.ligada)
    definirEntidadeId(novo.entidade_id === null ? "" : String(novo.entidade_id))
    definirLimite(String(novo.limite))
  }

  const carregar = useCallback(() => {
    definirFalha(null)
    api
      .importacaoAutomatica()
      .then(receber)
      .catch((erro: unknown) => definirFalha(mensagemDeRecusa(erro)))
  }, [])
  useEffect(carregar, [carregar])

  const emAndamento = estado?.em_andamento ?? false
  useEffect(() => {
    if (!emAndamento) return
    const intervalo = setInterval(() => {
      api
        .importacaoAutomatica()
        .then(definirEstado)
        .catch(() => undefined)
    }, 5000)
    return () => clearInterval(intervalo)
  }, [emAndamento])

  const importarAgora = async () => {
    definirGravando(true)
    try {
      definirEstado(await api.importarAgora())
      toast.success("Importação iniciada")
    } catch (erro) {
      toast.error(mensagemDeRecusa(erro))
    } finally {
      definirGravando(false)
    }
  }

  const gravar = async () => {
    if (estado === null) return
    const antes = estado.ligada
    definirGravando(true)
    try {
      receber(
        await api.mudarImportacaoAutomatica({
          ligada,
          entidade_id: entidadeId === "" ? null : Number(entidadeId),
          limite: Number(limite),
        }),
      )
      toast.success(ligada === antes ? "Importação salva" : ligada ? "Importação ligada" : "Importação desligada")
    } catch (erro) {
      toast.error(mensagemDeRecusa(erro))
    } finally {
      definirGravando(false)
    }
  }

  if (estado === null) {
    return (
      <Bloco titulo={TITULO_DA_IMPORTACAO}>
        {falha !== null ? <FalhaNaSecao mensagem={falha} aoTentar={carregar} /> : <CarregandoNaSecao />}
      </Bloco>
    )
  }

  const faltaDoAthus = estado.falta.filter((f) => f.startsWith("ATHUS_"))
  const mudou =
    ligada !== estado.ligada ||
    entidadeId !== (estado.entidade_id === null ? "" : String(estado.entidade_id)) ||
    limite !== String(estado.limite)
  const faltaParaSalvar = [
    ...(ligada ? faltaDoAthus : []),
    ...(ligada && entidadeId === "" ? ["a entidade que recebe os contratos"] : []),
    ...(Number(limite) < 1 ? ["o limite de documentos"] : []),
  ]
  const travado = estado.em_andamento
  const motivoSemSalvar = travado ? "Há uma importação em andamento" : !mudou ? "Nada mudou" : faltaParaSalvar.length > 0 ? `Falta ${faltaParaSalvar.join(", ")}` : undefined
  const motivoSemImportar = estado.em_andamento
    ? "Já há uma importação em andamento"
    : estado.falta.length > 0
      ? `Falta ${estado.falta.join(", ")}`
      : mudou
        ? "Salve as mudanças antes de importar"
        : undefined
  const salvar = () => {
    if (ligada !== estado.ligada) definirConfirmando(ligada ? "ligar" : "desligar")
    else void gravar()
  }
  const ultima = estado.ultima
  const entidadeEscolhida = estado.entidades.find((e) => String(e.id) === entidadeId)

  const TEXTO_DA_CONFIRMACAO: Record<Confirmacao, { titulo: string; texto: string; acao: string }> = {
    ligar: {
      titulo: "Ligar a importação automática?",
      texto: `A rodada diária passa a consultar o ATHUS e a ler até ${inteiro(Number(limite))} documentos para ${entidadeEscolhida?.sigla ?? "a entidade escolhida"}, o que pesa no sistema da entidade.`,
      acao: "Ligar",
    },
    desligar: {
      titulo: "Desligar a importação automática?",
      texto: "Os contratos deixam de chegar sozinhos do ATHUS. O que já foi importado continua, e o Root ainda pode importar agora.",
      acao: "Desligar",
    },
    importar: {
      titulo: "Importar agora?",
      texto: `Consulta o ATHUS e lê até ${inteiro(estado.limite)} documentos para ${estado.entidade_nome ?? "a entidade salva"}. Pode levar alguns minutos, e o andamento aparece aqui.`,
      acao: "Importar",
    },
  }

  const desfazer = () => receber(estado)
  const horario = estado.horario_da_rodada ?? estado.proxima_rodada ?? null
  const descricaoDaRodada =
    `${horario !== null ? `Todo dia às ${hora(horario)}, a rodada` : "A rodada diária"} traz os contratos do ATHUS antes de calcular a vigência e os avisos.` +
    (estado.ligada && estado.proxima_rodada ? ` Próxima rodada: ${data(estado.proxima_rodada)} às ${hora(estado.proxima_rodada)}.` : "")
  const importarAgoraBotao = estado.pode_mudar && (
    <Button
      type="button"
      size="sm"
      disabled={gravando || !estado.pode_importar_agora || mudou}
      title={motivoSemImportar}
      onClick={() => definirConfirmando("importar")}
    >
      <Icones.ImportarDaNuvem />
      Importar agora
    </Button>
  )

  return (
    <Bloco
      titulo={TITULO_DA_IMPORTACAO}
      dica="Ligada, a rodada diária do sistema importa os contratos do ATHUS, lê os documentos e só depois calcula a vigência e envia os avisos. Fica desligada até a administração da plataforma ligar, para não pesar no sistema da entidade sem necessidade. O Root também pode importar agora, com a entidade e o limite salvos, ligada ou não."
      aoLado={!estado.pode_mudar && <Badge tom={estado.ligada ? "verde" : "cinza"}>{estado.ligada ? "Ligada" : "Desligada"}</Badge>}
    >
      {estado.pode_mudar ? (
          <form
            className="flex flex-col gap-4 rounded-md border border-(--borda) p-4"
            onSubmit={(evento) => {
              evento.preventDefault()
              if (motivoSemSalvar === undefined && !gravando) salvar()
            }}
          >
            <div className="flex items-start justify-end gap-3">
              <div className="flex flex-col items-end gap-0.5 text-right">
                <label htmlFor="importacao-ligada" className="cursor-pointer text-sm font-medium text-(--tinta)">
                  Importar todo dia
                </label>
                <p id="importacao-ligada-descricao" className="m-0 text-sm text-(--tinta-2)">
                  {descricaoDaRodada}
                </p>
              </div>
              <Switch
                id="importacao-ligada"
                className="mt-0.5"
                checked={ligada}
                disabled={travado}
                onCheckedChange={definirLigada}
                aria-label="Importar todo dia"
                aria-describedby="importacao-ligada-descricao"
              />
            </div>
            <div className="flex flex-wrap gap-3">
              <div className="flex-[2_1_280px]">
                <RotuloDoCampo htmlFor="importacao-entidade" obrigatorio>
                  Entidade que recebe os contratos
                </RotuloDoCampo>
                <Escolha
                  id="importacao-entidade"
                  obrigatorio
                  desabilitado={travado}
                  valor={entidadeId}
                  aoMudar={definirEntidadeId}
                  vazio="Escolha a entidade"
                  placeholderDaBusca="Buscar por sigla ou nome"
                  opcoes={estado.entidades.map((entidade) => ({ valor: String(entidade.id), rotulo: `${entidade.sigla} · ${entidade.nome}` }))}
                />
              </div>
              <div className="flex-[1_1_160px]">
                <RotuloDoCampo htmlFor="importacao-limite" obrigatorio>
                  Documentos por importação
                </RotuloDoCampo>
                <Input
                  id="importacao-limite"
                  type="number"
                  min={1}
                  required
                  disabled={travado}
                  value={limite}
                  onChange={(evento) => definirLimite(evento.target.value)}
                />
              </div>
            </div>
            {faltaDoAthus.length > 0 && (
              <p className="m-0 text-sm text-(--tinta-2)">Para ligar, falta configurar no ATHUS: {faltaDoAthus.join(", ")}.</p>
            )}
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-(--borda) pt-4">
              {mudou && !travado && (
                <Button type="button" variant="outline" className={BOTAO_BRANCO} disabled={gravando} onClick={desfazer}>
                  Desfazer mudanças
                </Button>
              )}
              <Button type="submit" disabled={gravando || motivoSemSalvar !== undefined} title={motivoSemSalvar}>
                <Icones.Salvar />
                {gravando ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </form>
        ) : (
          <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-md border border-(--borda) p-4 text-sm">
            {horario !== null && (
              <>
                <dt className="text-(--tinta-2)">Rodada diária</dt>
                <dd className="m-0">{`Todo dia às ${hora(horario)}`}</dd>
              </>
            )}
            {estado.ligada && estado.proxima_rodada && (
              <>
                <dt className="text-(--tinta-2)">Próxima rodada</dt>
                <dd className="m-0">{`${data(estado.proxima_rodada)} às ${hora(estado.proxima_rodada)}`}</dd>
              </>
            )}
            <dt className="text-(--tinta-2)">Entidade que recebe os contratos</dt>
            <dd className="m-0">{estado.entidade_nome ?? "—"}</dd>
            <dt className="text-(--tinta-2)">Documentos por importação</dt>
            <dd className="m-0">{inteiro(estado.limite)}</dd>
          </dl>
        )}

      {estado.em_andamento && (
        <Parte titulo="Andamento">
          <Andamento desde={estado.andamento_desde} andamento={estado.andamento ?? null} />
        </Parte>
      )}

      <Parte
        titulo="Última importação"
        acoes={
          <>
            <Button type="button" variant="outline" size="sm" className={BOTAO_CINZA} onClick={() => definirVendoHistorico(true)}>
              <Icones.HistoricoDeExecucoes />
              Histórico
            </Button>
            {importarAgoraBotao}
          </>
        }
      >
        <div className="rounded-md bg-muted p-3 text-sm">
          {ultima === null ? (
            <span className="text-(--tinta-2)">Nenhuma importação rodou ainda.</span>
          ) : (
            <>
              <span className="font-semibold">
                {dataHora(ultima.iniciada_em)}
                {ultima.entidade_sigla !== null && ` · ${ultima.entidade_sigla}`}
              </span>
              <span className="block text-(--tinta-2)">
                {ultima.origem !== "tela"
                  ? "Pelo relógio"
                  : ultima.pedida_por_nome !== null
                    ? `Pedida por ${ultima.pedida_por_nome}`
                    : "Pedida pela tela"}
              </span>
              {ultima.interrompida ? (
                <span className="block text-destructive">Interrompida antes de terminar</span>
              ) : ultima.falha !== null ? (
                <span className="block text-destructive first-letter:uppercase">{ultima.falha}</span>
              ) : (
                <span className="block text-(--tinta-2)">
                  {[
                    plural(ultima.contratos_criados, "contrato novo", "contratos novos"),
                    plural(ultima.contratos_atualizados, "atualizado", "atualizados"),
                    plural(ultima.pdfs_lidos, "documento lido", "documentos lidos"),
                    plural(ultima.falhas_de_leitura, "falha de leitura", "falhas de leitura"),
                    plural(ultima.contratados_aguardando, "contratado aguardando confirmação", "contratados aguardando confirmação"),
                  ].join(" · ")}
                </span>
              )}
              {(ultima.falhas ?? []).length > 0 && (
                <Collapsible>
                  <CollapsibleTrigger asChild>
                    <Button type="button" variant="link" size="sm" className="h-auto px-0">
                      Ver as falhas de leitura
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <ul className="m-0 mt-1 flex list-disc flex-col gap-1 pl-5 text-(--tinta)">
                      {(ultima.falhas ?? []).map((falha, indice) => (
                        <li key={indice} className="first-letter:uppercase">
                          {falha}
                        </li>
                      ))}
                    </ul>
                  </CollapsibleContent>
                </Collapsible>
              )}
            </>
          )}
        </div>
      </Parte>

      <AlertDialog open={confirmando !== null} onOpenChange={(aberto) => !aberto && definirConfirmando(null)}>
        <AlertDialogContent>
          <AlertDialogHeader tom={confirmando === "desligar" ? "perigo" : undefined}>
            <AlertDialogTitle>{confirmando !== null ? TEXTO_DA_CONFIRMACAO[confirmando].titulo : ""}</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            <AlertDialogDescription>{confirmando !== null ? TEXTO_DA_CONFIRMACAO[confirmando].texto : ""}</AlertDialogDescription>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button type="button" variant="outline">
                <Icones.Fechar />
                Cancelar
              </Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button
                type="button"
                variant={confirmando === "desligar" ? "destructive" : "default"}
                onClick={() => {
                  if (confirmando === "importar") void importarAgora()
                  else void gravar()
                  definirConfirmando(null)
                }}
              >
                {confirmando !== null ? TEXTO_DA_CONFIRMACAO[confirmando].acao : ""}
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Sheet open={vendoHistorico} onOpenChange={definirVendoHistorico}>
        <SheetContent
          aria-describedby={undefined}
          className="w-full gap-0 overflow-y-auto bg-card p-0 sm:max-w-2xl [&>button:last-child]:text-primary-foreground [&>button:last-child]:opacity-100"
        >
          <SheetHeader className={CABECALHO_DO_SHEET}>
            <SheetTitle className="text-primary-foreground">Histórico de importações</SheetTitle>
          </SheetHeader>
          {vendoHistorico && <HistoricoDaImportacao />}
        </SheetContent>
      </Sheet>
    </Bloco>
  )
}

function resultadoDaExecucao(execucao: ExecucaoDaImportacao): string {
  if (execucao.interrompida) return "Interrompida antes de terminar"
  if (execucao.falha !== null) return execucao.falha
  return [
    plural(execucao.contratos_criados, "novo", "novos"),
    plural(execucao.contratos_atualizados, "atualizado", "atualizados"),
    plural(execucao.pdfs_lidos, "lido", "lidos"),
    plural(execucao.falhas_de_leitura, "falha", "falhas"),
  ].join(" · ")
}

function HistoricoDaImportacao() {
  const [execucoes, definirExecucoes] = useState<ExecucaoDaImportacao[] | null>(null)
  const [falha, definirFalha] = useState<string | null>(null)
  const carregar = useCallback(() => {
    definirFalha(null)
    api
      .historicoDaImportacao()
      .then(definirExecucoes)
      .catch((erro: unknown) => definirFalha(mensagemDeRecusa(erro)))
  }, [])
  useEffect(carregar, [carregar])

  if (falha !== null)
    return (
      <div className="px-6 pt-3 pb-6">
        <FalhaNaSecao mensagem={falha} aoTentar={carregar} />
      </div>
    )
  if (execucoes === null)
    return (
      <div className="px-6 pt-3 pb-6">
        <CarregandoNaSecao />
      </div>
    )
  return (
    <div className="px-6 pt-3 pb-6">
      <TablePrincipal2
        data={execucoes}
        paginacao={false}
        pageCount={1}
        currentPage={1}
        onPageChange={() => undefined}
        listrada
        mensagemVazia="Nenhuma importação rodou ainda."
      >
        <Column<ExecucaoDaImportacao> name="iniciada_em" label="Quando" enableSorting={false} render={(valor) => <span className="whitespace-nowrap">{dataHora(String(valor))}</span>} />
        <Column<ExecucaoDaImportacao>
          name="origem"
          label="Origem"
          enableSorting={false}
          render={(_valor, execucao) =>
            execucao.origem !== "tela" ? "Pelo relógio" : execucao.pedida_por_nome !== null ? `Pedida por ${execucao.pedida_por_nome}` : "Pedida pela tela"
          }
        />
        <Column<ExecucaoDaImportacao> name="entidade_sigla" label="Entidade" enableSorting={false} render={(valor) => (valor === null ? "—" : String(valor))} />
        <Column<ExecucaoDaImportacao>
          name="falha"
          label="Resultado"
          enableSorting={false}
          render={(_valor, execucao) => (
            <span className={cn(execucao.interrompida || execucao.falha !== null ? "text-destructive" : undefined, "first-letter:uppercase")}>
              {resultadoDaExecucao(execucao)}
            </span>
          )}
        />
      </TablePrincipal2>
    </div>
  )
}

const CATEGORIAS_POR_PAGINA = 10

const ABAS_DAS_CATEGORIAS: DescricaoDeAba<"todas" | "configuradas">[] = [
  { chave: "todas", rotulo: "Todas", dica: "Mostra todas as categorias do ATHUS" },
  { chave: "configuradas", rotulo: "Configuradas", dica: "Mostra só as categorias que a importação lê" },
]

const semAcento = (texto: string) =>
  texto
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim()

function AlternarSecao({ aberta, rotulo }: { aberta: boolean; rotulo: string }) {
  return (
    <CollapsibleTrigger asChild>
      <Button type="button" variant="ghost" size="sm">
        {aberta ? "Esconder" : "Mostrar"} {rotulo}
        <span className={cn("flex transition-transform motion-reduce:transition-none", aberta && "rotate-180")}>
          <Icones.Aumentar tamanho={16} />
        </span>
      </Button>
    </CollapsibleTrigger>
  )
}

function CategoriasDeProcesso() {
  const [categorias, definirCategorias] = useState<CategoriasDoAthus | null>(null)
  const [falha, definirFalha] = useState<string | null>(null)
  const [busca, definirBusca] = useState("")
  const [pagina, definirPagina] = useState(1)
  const [porPagina, definirPorPagina] = useState(CATEGORIAS_POR_PAGINA)
  const [aberta, definirAberta] = useState(false)
  const [aba, definirAba] = useState<"todas" | "configuradas">("todas")

  const carregar = useCallback(() => {
    definirFalha(null)
    api
      .categoriasDoAthus()
      .then(definirCategorias)
      .catch((erro: unknown) => definirFalha(mensagemDeRecusa(erro)))
  }, [])
  useEffect(carregar, [carregar])

  const procurado = semAcento(busca)
  const ordenadas =
    categorias === null
      ? []
      : [...categorias.categorias]
          .sort((a, b) => Number(b.configurada) - Number(a.configurada))
          .filter((c) => (aba === "todas" || c.configurada) && (procurado === "" || semAcento(c.nome).includes(procurado)))
  const totalDePaginas = Math.max(1, Math.ceil(ordenadas.length / porPagina))
  const paginaAtual = Math.min(pagina, totalDePaginas)
  const recorte = ordenadas.slice((paginaAtual - 1) * porPagina, paginaAtual * porPagina)
  const configuradas = categorias?.categorias.filter((c) => c.configurada).length ?? 0

  return (
    <Collapsible open={aberta} onOpenChange={definirAberta}>
      <Parte
        titulo="Categorias de processo"
        dica="A importação lê só os processos das categorias configuradas pelo setor de TI. A lista vem do próprio ATHUS e serve para conferir o nome de cada categoria."
        acoes={categorias !== null && categorias.disponivel ? <AlternarSecao aberta={aberta} rotulo="as categorias" /> : undefined}
      >
        {falha !== null ? (
          <FalhaNaSecao mensagem={`Não foi possível consultar: ${falha}`} aoTentar={carregar} />
        ) : categorias === null ? (
          <div aria-busy="true">
            <Skeleton className="h-5 w-64" />
          </div>
        ) : !categorias.disponivel ? (
          <Alert>
            <Icones.Informacao />
            <AlertTitle>O ATHUS não informou as categorias</AlertTitle>
            <AlertDescription className="first-letter:uppercase">{categorias.motivo}</AlertDescription>
          </Alert>
        ) : (
          <>
            <p className="m-0 text-sm text-(--tinta-2)">
              {`${plural(configuradas, "categoria configurada", "categorias configuradas")} de ${inteiro(categorias.categorias.length)}`}
            </p>
            {categorias.nao_achadas.length > 0 && (
              <Alert variant="destructive">
                <Icones.Erro />
                <AlertTitle>Nome não encontrado</AlertTitle>
                <AlertDescription>
                  {`${categorias.nao_achadas.length === 1 ? "A categoria configurada " : "As categorias configuradas "}${categorias.nao_achadas
                    .map((nome) => `"${nome}"`)
                    .join(", ")}${categorias.nao_achadas.length === 1 ? " não existe" : " não existem"} no ATHUS. A importação recusa até o setor de TI corrigir o nome.`}
                </AlertDescription>
              </Alert>
            )}
            {categorias.configuradas.length === 0 && (
              <Alert>
                <Icones.Risco />
                <AlertTitle>Nenhuma categoria configurada</AlertTitle>
                <AlertDescription>Sem ela, a importação não lê nada. Peça ao setor de TI para configurar.</AlertDescription>
              </Alert>
            )}
            <CollapsibleContent>
              <TablePrincipal2
                data={recorte}
                pageCount={totalDePaginas}
                currentPage={paginaAtual}
                pageSize={porPagina}
                totalRegistros={ordenadas.length}
                onPageChange={definirPagina}
                onPageSizeChange={(tamanho) => {
                  definirPorPagina(tamanho)
                  definirPagina(1)
                }}
                globalFilterValue={busca}
                onGlobalFilterChange={(valor) => {
                  definirBusca(valor)
                  definirPagina(1)
                }}
                listrada
                mensagemVazia={busca !== "" ? `Nada casa com "${busca}".` : "Nenhuma categoria nesta situação."}
                abasNaBarra={
                  <Abas
                    nome="categorias-do-athus"
                    rotulo="Situação das categorias"
                    classe="mb-0"
                    abas={ABAS_DAS_CATEGORIAS}
                    atual={aba}
                    aoTrocar={(chave) => {
                      definirAba(chave)
                      definirPagina(1)
                    }}
                  />
                }
              >
                <TablePrincipalFiltroGlobal globalFilter={busca} setGlobalFilter={definirBusca} placeholder="Categoria" />
                <Column<CategoriaDoAthus>
                  name="nome"
                  label="Categoria"
                  enableSorting={false}
                  render={(_valor, categoria) => (
                    <span className={categoria.configurada ? "font-semibold" : undefined}>
                      {categoria.nome}
                      {categoria.configurada && categoria.grupos.length > 0 && (
                        <Collapsible>
                          <CollapsibleTrigger asChild>
                            <Button type="button" variant="link" size="sm" className="h-auto px-0 font-normal">
                              {`Ver os ${inteiro(categoria.grupos.length)} grupos`}
                            </Button>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <span className="block font-normal text-(--tinta-2)">
                              {categoria.grupos.map((grupo) => grupo.nome).join(" · ")}
                            </span>
                          </CollapsibleContent>
                        </Collapsible>
                      )}
                    </span>
                  )}
                />
                <Column<CategoriaDoAthus>
                  name="grupos"
                  label="Grupos"
                  enableSorting={false}
                  render={(_valor, categoria) => <span className="tabular-nums">{inteiro(categoria.grupos.length)}</span>}
                />
                <Column<CategoriaDoAthus>
                  name="configurada"
                  label="Situação"
                  enableSorting={false}
                  render={(_valor, categoria) =>
                    categoria.configurada ? <Badge tom="verde">Configurada</Badge> : <span className="text-(--tinta-2)">Não configurada</span>
                  }
                />
              </TablePrincipal2>
            </CollapsibleContent>
          </>
        )}
      </Parte>
    </Collapsible>
  )
}

function Conexao({
  espelho,
  falha,
  aoTentar,
}: {
  espelho: EspelhoDoAthus | null
  falha: string | null
  aoTentar: () => void
}) {
  const [testando, definirTestando] = useState(false)
  const testar = async () => {
    definirTestando(true)
    try {
      const resultado = await api.testarAthus()
      if (resultado.respondeu) toast.success(resultado.mensagem)
      else toast.error(resultado.mensagem)
    } catch (erro) {
      toast.error(mensagemDeRecusa(erro))
    } finally {
      definirTestando(false)
    }
  }
  const copiar = async (endereco: string) => {
    try {
      await navigator.clipboard.writeText(endereco)
      toast.success("Endereço copiado")
    } catch {
      toast.error("Não foi possível copiar o endereço")
    }
  }

  return (
    <Bloco
      titulo="Conexão com o ATHUS"
      aoLado={espelho !== null && <Selo situacao={espelho.ligado ? "ligado" : "desligado"} />}
      acoes={
        espelho !== null && (
          <Button type="button" variant="outline" size="sm" className={BOTAO_DE_ACAO} disabled={testando} onClick={() => void testar()}>
            {testando ? "Testando..." : "Testar conexão"}
          </Button>
        )
      }
    >
      {espelho === null ? (
        falha !== null ? (
          <FalhaNaSecao mensagem={`Não foi possível carregar: ${falha}`} aoTentar={aoTentar} />
        ) : (
          <CarregandoNaSecao />
        )
      ) : (
        <>
          <Numeros>
            <Numero rotulo="Contratos vindos do ATHUS" valor={inteiro(espelho.espelhados)} nota="vindos do sistema de processos" />
            <Numero rotulo="Com documento lido" valor={inteiro(espelho.com_leitura_de_pdf)} nota="o documento é o que vale legalmente" />
            <Numero rotulo="Termos aditivos" valor={inteiro(espelho.aditivos)} nota={`e ${inteiro(espelho.apostilamentos)} apostilamentos`} />
            <Numero rotulo="Cadastrados aqui" valor={inteiro(espelho.proprios)} nota="não vieram do ATHUS" />
          </Numeros>

          <div className="divide-y divide-border overflow-x-auto rounded-md border border-border">
            <LinhaDaLigacao Icone={Icones.Servidor} rotulo="Endereço do ATHUS">
              <span className="flex items-center gap-1">
                <span className="block flex-1 rounded-md bg-muted px-2 py-1 font-mono">{espelho.base_da_api ?? "—"}</span>
                {espelho.base_da_api !== null && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Copiar o endereço do ATHUS"
                    title="Copia o endereço do ATHUS"
                    onClick={() => void copiar(espelho.base_da_api ?? "")}
                  >
                    <Icones.Copiar />
                  </Button>
                )}
              </span>
            </LinhaDaLigacao>

            <LinhaDaLigacao Icone={Icones.BancoDeDados} rotulo="Banco de dados">
              <Badge className="mr-2">
                <Icones.Cadeado />
                Somente leitura
              </Badge>
              Nada é alterado no ATHUS: o sistema só consulta.
            </LinhaDaLigacao>

            <LinhaDaLigacao Icone={Icones.Armazenamento} rotulo="Arquivos">
              Os documentos continuam no ATHUS e abrem de lá quando você os consulta.
            </LinhaDaLigacao>
          </div>
        </>
      )}
    </Bloco>
  )
}

function OQueNuncaSai({ colunas }: { colunas: string[] }) {
  const [aberta, definirAberta] = useState(false)
  return (
    <Collapsible open={aberta} onOpenChange={definirAberta}>
      <Parte
        titulo="O que nunca sai do ATHUS"
        dica="Dados de rastreamento do servidor público que existem no ATHUS e que este sistema escolheu não trazer, para garantir privacidade e segurança."
        acoes={colunas.length > 0 ? <AlternarSecao aberta={aberta} rotulo="os dados" /> : undefined}
      >
        {colunas.length === 0 ? (
          <p className="m-0 text-sm text-(--tinta-2)">Não foi possível ler esta lista neste sistema.</p>
        ) : (
          <>
            <p className="m-0 text-sm text-(--tinta-2)">
              {plural(colunas.length, "dado de rastreamento fica no ATHUS", "dados de rastreamento ficam no ATHUS")}
            </p>
            <CollapsibleContent>
              <ul className="m-0 grid list-none grid-cols-1 gap-x-6 gap-y-2 p-0 sm:grid-cols-2 lg:grid-cols-3">
                {colunas.map((coluna) => (
                  <li key={coluna} className="flex items-center gap-2 text-sm text-(--tinta)">
                    <span className="flex text-(--tinta-3)">
                      <Icones.Oculto />
                    </span>
                    <span className="first-letter:uppercase">{DADO_QUE_NAO_SAI[coluna] ?? "Dado sigiloso protegido"}</span>
                  </li>
                ))}
              </ul>
            </CollapsibleContent>
          </>
        )}
      </Parte>
    </Collapsible>
  )
}

export function Athus() {
  const [espelho, definirEspelho] = useState<EspelhoDoAthus | null>(null)
  const [falha, definirFalha] = useState<string | null>(null)

  const carregar = useCallback(() => {
    definirFalha(null)
    api
      .athus()
      .then(definirEspelho)
      .catch((erro: unknown) => definirFalha(mensagemDeRecusa(erro)))
  }, [])
  useEffect(carregar, [carregar])

  return (
    <div className="flex flex-col divide-y divide-(--borda)">
      <ImportacaoNoRelogio />
      <Conexao espelho={espelho} falha={falha} aoTentar={carregar} />
      <Bloco
        titulo="Escopo e privacidade"
        dica="O que a importação lê do ATHUS e o que fica protegido lá, sem nunca entrar neste sistema."
      >
        <CategoriasDeProcesso />
        <div className="border-t border-(--borda) pt-4">
          {espelho !== null ? (
            <OQueNuncaSai colunas={espelho.colunas_que_nunca_saem} />
          ) : (
            <Parte titulo="O que nunca sai do ATHUS">
              <div aria-busy="true">
                <Skeleton className="h-5 w-64" />
              </div>
            </Parte>
          )}
        </div>
      </Bloco>
    </div>
  )
}
